/*
 * Testimonia — variant picker fallback
 *
 * Makes the product page sellable when the Swatch King app fails to draw its controls.
 *
 * The problem this solves, verified on the live store:
 *
 *   The app ships a "pre-hide" stylesheet that fires before anything else and hides the theme's
 *   own picker so the two are never visible at once:
 *
 *     html:not(.nojs):not(.no-js) variant-radios,
 *     html:not(.nojs):not(.no-js) variant-selects,
 *     … 17 more selectors … { opacity: 0; max-height: 0 }
 *
 *   That rule is removed by the app's own JavaScript once it has mounted. So the native picker
 *   being usable is CONDITIONAL on the app succeeding. If the app's script 404s, throws, or its
 *   call to api.starapps.studio fails, nothing removes the rule: the app draws no swatches AND
 *   the theme's picker stays at zero opacity and zero height.
 *
 *   The customer is then left with a product page carrying no options at all. The add-to-cart
 *   button is still there, so the page does not look broken — it just cannot be used to choose
 *   a frame, a paspartú or a size, and for a made-to-order catalogue that means no sale.
 *
 * What this does:
 *
 *   Waits for the app. If it has not rendered a single control by the time the grace period is
 *   up, it overrides the pre-hide rule so the theme's own picker becomes visible and the
 *   customer can buy. Nothing else changes: the native picker is the theme's, it drives the
 *   form, and it is what the theme has always submitted.
 *
 * Why it cannot make things worse:
 *
 *   - It only ever ADDS visibility, and only when the app has drawn nothing.
 *   - It keeps watching. If the app arrives late, the override is withdrawn, so the two pickers
 *     are never on screen together.
 *   - If the app is not installed on a template at all, there is no pre-hide rule to fight and
 *     the native picker is already visible; the override is then a no-op.
 *
 * Loaded from sections/testimonia-pdp-styles.liquid, which renders on every product template.
 */
(function () {
  'use strict';

  // How long the app gets before we assume it is not coming. Measured on the live store, Swatch
  // King mounts about 400-900 ms after DOMContentLoaded on a cuadro; 4 s is generous enough that
  // a slow connection is never mistaken for a failure.
  var GRACE_MS = 4000;

  // Keep looking afterwards, so an app that recovers still gets its controls back.
  var WATCH_MS = 20000;
  var POLL_MS = 500;

  var STYLE_ID = 'testimonia-picker-fallback';

  // The selectors the app's pre-hide rule targets that this theme actually renders. Overriding
  // only these keeps the blast radius to the product picker.
  var TARGETS = [
    'variant-radios',
    'variant-selects',
    '.product-form__variants',
    '.selector-wrapper',
  ];

  function appHasRendered() {
    var host = document.querySelector('variant-swatch-king');
    if (!host) return false;
    // .swatch-single is the app's per-option group. Its presence is the only reliable proof that
    // the app got past configuration and actually drew something.
    return host.querySelectorAll('.swatch-single').length > 0;
  }

  function nativePickerExists() {
    return !!document.querySelector('variant-radios, variant-selects');
  }

  function showNativePicker() {
    if (document.getElementById(STYLE_ID)) return;

    var css = TARGETS.map(function (sel) {
      // `html:not(.nojs):not(.no-js)` is the app's own prefix; matching it and adding one more
      // class-level selector wins on specificity, and !important covers the rest.
      return 'html ' + sel;
    }).join(',\n') + ' {\n  opacity: 1 !important;\n  max-height: none !important;\n}\n';

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);

    if (window.console && console.warn) {
      console.warn(
        '[Testimonia] Swatch King no renderizó sus controles; se muestra el selector nativo del ' +
        'theme para que el producto siga siendo comprable.'
      );
    }
  }

  function hideNativePicker() {
    var style = document.getElementById(STYLE_ID);
    if (style && style.parentNode) style.parentNode.removeChild(style);
  }

  function start() {
    // Nothing to rescue on a page with no picker (default-variant products, for one).
    if (!nativePickerExists()) return;

    setTimeout(function () {
      if (appHasRendered()) {
        watchForRegression();
        return;
      }

      showNativePicker();

      // The app may still arrive. Withdraw the override if it does.
      var waited = 0;
      var timer = setInterval(function () {
        waited += POLL_MS;
        if (appHasRendered()) {
          hideNativePicker();
          clearInterval(timer);
          watchForRegression();
          return;
        }
        if (waited >= WATCH_MS) clearInterval(timer);
      }, POLL_MS);
    }, GRACE_MS);
  }

  /**
   * The theme re-fetches and replaces the product info block on every variant change
   * (renderProductInfo, assets/global.js:2547). The replacement markup carries the theme's
   * picker but none of the app's, so if the app does not re-draw after a swap the page ends up
   * in the same unusable state mid-session, not just on load.
   */
  function watchForRegression() {
    var host = document.querySelector('.product-parameters') || document.body;
    var pending = null;

    var observer = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (!nativePickerExists()) return;
        if (appHasRendered()) hideNativePicker();
        else showNativePicker();
      }, 1200);
    });

    observer.observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
